/* global localStorage, document */
import { AuthContext } from "../types";

const USER_STORE_KEY = "user-storage";
const AUTH_STORE_KEYS = ["_usrCtx_907x", "_usrCtx_909x"];
const REFRESH_TOKEN_KEY = "refreshToken";
const MANUAL_AUTH_KEY = "juricloud_word_auth_context";

const parseJson = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getNestedUser = (store: unknown): { id?: string; tenantId?: string } => {
  const state = (store as any)?.state;
  const user = state?.user || (store as any)?.user;
  return {
    id: user?.id,
    tenantId: user?.tenantId,
  };
};

const getStoredUserContext = () => {
  const userStore = parseJson(localStorage.getItem(USER_STORE_KEY));
  const fromUserStore = getNestedUser(userStore);
  if (fromUserStore.id && fromUserStore.tenantId) {
    return fromUserStore;
  }

  for (const key of AUTH_STORE_KEYS) {
    const authStore = parseJson(localStorage.getItem(key));
    const fromAuthStore = getNestedUser(authStore);
    if (fromAuthStore.id && fromAuthStore.tenantId) {
      return fromAuthStore;
    }
  }

  return { id: undefined, tenantId: undefined };
};

const getCookieToken = () => {
  const cookie = document.cookie || "";
  const part = cookie
    .split(";")
    .map((v) => v.trim())
    .find((v) => v.startsWith("api_auth_token="));
  if (!part) {
    return "";
  }
  return decodeURIComponent(part.split("=")[1] || "");
};

const getStoredAccessToken = () => {
  const raw = localStorage.getItem("_usrCtx_909x");
  if (!raw) {
    return "";
  }
  const parsed = parseJson<{ state?: { token?: string }; token?: string }>(raw);
  if (parsed?.state?.token) {
    return parsed.state.token;
  }
  if (parsed?.token) {
    return parsed.token;
  }
  return raw;
};

export const getAuthContext = (): AuthContext => {
  const manualAuth = parseJson<AuthContext>(localStorage.getItem(MANUAL_AUTH_KEY));
  if (manualAuth?.baseUrl && manualAuth?.tenantId && manualAuth?.accessToken) {
    return {
      ...manualAuth,
      baseUrl: manualAuth.baseUrl.replace(/\/$/, ""),
    };
  }

  // Resolution order:
  //   1. Manual override in localStorage (set by QA/dev).
  //   2. JURICLOUD_API_URL injected at build time via webpack DefinePlugin.
  //   3. Dev fallback — webpack-dev-server proxies `/juricloud-api`.
  const baseUrl = (
    localStorage.getItem("juricloud_api_base_url") ||
    (typeof process !== "undefined" && process.env.JURICLOUD_API_URL) ||
    "/juricloud-api"
  ).replace(/\/$/, "");

  const { id, tenantId } = getStoredUserContext();
  if (!tenantId) {
    throw new Error("Unable to resolve tenantId from local storage.");
  }

  const accessToken = getCookieToken() || getStoredAccessToken();
  if (!accessToken) {
    throw new Error("Unable to resolve access token. Please sign in first.");
  }

  return {
    baseUrl,
    tenantId,
    userId: id,
    accessToken,
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || undefined,
  };
};

export const saveManualAuthContext = (value: AuthContext) => {
  localStorage.setItem(
    MANUAL_AUTH_KEY,
    JSON.stringify({
      baseUrl: value.baseUrl.replace(/\/$/, ""),
      tenantId: value.tenantId,
      userId: value.userId,
      accessToken: value.accessToken,
      refreshToken: value.refreshToken,
    })
  );
};

export const clearManualAuthContext = () => {
  localStorage.removeItem(MANUAL_AUTH_KEY);
};

export const hasResolvableAuth = (): boolean => {
  try {
    getAuthContext();
    return true;
  } catch {
    return false;
  }
};
